import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';

/* =============================================================
   Lumix Óticas — Estoque de Armações Clean
   Padrão visual: Google-like, DPI pequeno, multiempresa real.
   Sem lojas fixas, sem URL fixa, usando /empresas + /api/armacoes.
============================================================= */

const LOW_STOCK_LIMIT = 2;
const INITIAL_VISIBLE_ITEMS = 10;
const LOAD_MORE_STEP = 10;
const SCROLL_LOAD_OFFSET = 160;

const Icons = {
  Search: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Plus: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Edit: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  ),
  Trash: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  Dollar: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  Refresh: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21.5 2v6h-6" />
      <path d="M21.34 15.57a10 10 0 1 1-.59-9.21L21.5 8" />
    </svg>
  ),
  Transfer: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M7 7h14l-4-4" />
      <path d="M17 17H3l4 4" />
    </svg>
  ),
  Box: () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.29 8.83 12 13.71 20.71 8.83" />
      <line x1="12" y1="22.76" x2="12" y2="13.71" />
    </svg>
  ),
  ArrowLeft: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  Printer: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  ),
  Close: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Save: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Alert: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  Building: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M9 6h6M9 10h6M9 14h6M9 18h6" />
    </svg>
  ),
  Image: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  Upload: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  Check: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
};

/* =============================================================
   Helpers
============================================================= */

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(value || 0));

const safeParseUser = () => {
  try {
    return JSON.parse(localStorage.getItem('vizumUser')) || {};
  } catch {
    return {};
  }
};

const getEmpresaIdFromUser = (user = {}) => {
  const raw =
    user?.empresaId?._id ||
    user?.empresaId ||
    user?.empresa?._id ||
    user?.empresa?.id ||
    '';

  return raw ? String(raw) : '';
};

const montarParamsEmpresa = (empresaId) => {
  return empresaId ? { empresaId: String(empresaId) } : {};
};

const resolverArrayApi = (data, fallbackKey = '') => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (fallbackKey && Array.isArray(data?.[fallbackKey])) return data[fallbackKey];
  if (Array.isArray(data?.empresas)) return data.empresas;
  if (Array.isArray(data?.armacoes)) return data.armacoes;
  return [];
};

const normalizarTexto = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const normalizarId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
};

const mesmoId = (a, b) => {
  if (!a || !b) return false;
  return String(a) === String(b);
};

const parseNumber = (value) => {
  const normalized = String(value ?? '')
    .replace(/\./g, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');

  const number = Number(normalized);

  return Number.isFinite(number) ? number : 0;
};

const getEmpresaCodigo = (empresa = {}) =>
  String(empresa.codigoLoja || empresa.loja || empresa.unidade || empresa._id || empresa.id || '').trim();

const getEmpresaLabel = (empresa = {}) => {
  if (!empresa) return '';

  const nome = empresa.nome || empresa.razaoSocial || 'Empresa';
  const unidade = empresa.unidade && empresa.unidade !== 'Matriz' ? ` · ${empresa.unidade}` : '';

  return `${nome}${unidade}`;
};

const getEmpresaCurta = (empresa = {}) => {
  if (!empresa) return 'Sem loja';

  return empresa.unidade || empresa.codigoLoja || empresa.nome || 'Sem loja';
};

const getItemEmpresaId = (item = {}) =>
  normalizarId(item.empresaId || item.empresa?._id || item.empresa?.id || item.empresa || '');

const encontrarEmpresaDoItem = (item = {}, empresas = []) => {
  const empresaId = getItemEmpresaId(item);
  const loja = String(item.loja || '').trim();
  const lojaNormalizada = normalizarTexto(loja);

  return (
    empresas.find((empresa) => mesmoId(empresa._id, empresaId) || mesmoId(empresa.id, empresaId)) ||
    empresas.find((empresa) => normalizarTexto(getEmpresaCodigo(empresa)) === lojaNormalizada) ||
    empresas.find((empresa) => normalizarTexto(empresa.unidade) === lojaNormalizada) ||
    empresas.find((empresa) => normalizarTexto(empresa.nome) === lojaNormalizada) ||
    null
  );
};

const getLojaValue = (empresa = {}) => getEmpresaCodigo(empresa) || normalizarId(empresa._id || empresa.id);


const normalizarItemParaSalvar = (item = {}) => ({
  ...item,
  loja: String(item.loja || '').trim(),
  empresaId: item.empresaId || undefined,
  marca: String(item.marca || '').trim().toUpperCase(),
  material: String(item.material || '').trim(),
  ref: String(item.ref || '').trim(),
  aro: String(item.aro || '').trim(),
  ponte: String(item.ponte || '').trim(),
  haste: String(item.haste || '').trim(),
  cor: String(item.cor || '').trim(),
  qtd: Number(item.qtd || 0),
  preco: parseNumber(item.preco)
});

const getDataAtualBR = () =>
  new Date().toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

const getImagemProtegidaEndpoint = (itemOuId) => {
  const id = typeof itemOuId === 'object'
    ? normalizarId(itemOuId?._id || itemOuId?.id)
    : normalizarId(itemOuId);

  return id ? `/api/armacoes/${id}/imagem` : '';
};

const criarItemVazio = (loja = '', empresaId = '') => ({
  loja,
  empresaId,
  marca: '',
  material: '',
  ref: '',
  aro: '',
  ponte: '',
  haste: '',
  cor: '',
  qtd: 1,
  preco: 0,
  checado: false
});

const montarFormDataArmacao = (item = {}, imagemFile = null) => {
  const normalizado = normalizarItemParaSalvar(item);
  const formData = new FormData();

  Object.entries(normalizado).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });

  if (imagemFile) {
    formData.append('imagem', imagemFile);
  }

  return formData;
};

const getMensagemErroApi = (error, fallback = 'Erro ao processar solicitação.') => {
  return (
    error.response?.data?.message ||
    error.response?.data?.error ||
    error.response?.data?.erro ||
    fallback
  );
};

const revogarPreviewBlob = (url = '') => {
  if (String(url || '').startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

/* =============================================================
   Componentes pequenos
============================================================= */

function LoadingPanel() {
  return (
    <div className="stock-loading">
      <div className="thin-loader" />
      <span>Sincronizando estoque...</span>
    </div>
  );
}

function KpiCell({ icon, label, value, help, tone = 'neutral' }) {
  return (
    <div className={`kpi-cell ${tone}`}>
      <div className="kpi-icon">{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
      {help ? <small>{help}</small> : null}
    </div>
  );
}

function LojaBadge({ item, empresas }) {
  const empresa = encontrarEmpresaDoItem(item, empresas);
  const label = empresa ? getEmpresaCurta(empresa) : item.loja || 'Sem loja';

  return (
    <span className="loja-badge">
      <Icons.Building />
      {label}
    </span>
  );
}

function Modal({ title, subtitle, children, onClose, width = 620 }) {
  return (
    <div
      className="modal-overlay no-print"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal-shell" style={{ width: `min(${width}px, 96vw)` }}>
        <div className="modal-head">
          <div>
            <span className="kicker">Estoque Lumix</span>
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>

          <button type="button" className="plain-icon-btn" onClick={onClose}>
            <Icons.Close />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder = '', inputMode }) {
  return (
    <label className="form-field">
      <span>{label}</span>
      <input
        type={type}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, children }) {
  return (
    <label className="form-field">
      <span>{label}</span>
      <select value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
        {children}
      </select>
    </label>
  );
}

function ImagePicker({
  label = 'Imagem da armação',
  preview = '',
  hasImage = false,
  onChange,
  onRemove,
  onOpen,
  help = 'JPG, PNG ou WEBP. O backend comprime e entrega a imagem por rota protegida.'
}) {
  const temImagem = Boolean(preview || hasImage);

  return (
    <div className="image-picker">
      <button
        type="button"
        className={`image-picker-preview ${preview ? 'clickable' : ''}`}
        onClick={() => {
          if (preview && typeof onOpen === 'function') onOpen();
        }}
        disabled={!preview}
        title={preview ? 'Clique para ampliar' : 'Sem imagem para visualizar'}
      >
        {preview ? (
          <img src={preview} alt="Prévia da armação" />
        ) : (
          <div className="image-placeholder">
            <Icons.Image />
            <span>{temImagem ? 'Imagem protegida' : 'Sem imagem'}</span>
          </div>
        )}
      </button>

      <div className="image-picker-info">
        <span>{label}</span>
        <small>{help}</small>

        <div className="image-picker-actions">
          <label className="upload-btn">
            <Icons.Upload />
            Selecionar imagem
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={(event) => onChange(event.target.files?.[0] || null)}
            />
          </label>

          {preview ? (
            <button type="button" className="plain-btn" onClick={onOpen}>
              Ampliar
            </button>
          ) : null}

          {temImagem ? (
            <button type="button" className="plain-btn danger" onClick={onRemove}>
              Remover
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ImageViewerModal({ item, imageUrl, onClose }) {
  if (!imageUrl) return null;

  return (
    <div
      className="image-viewer-overlay no-print"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="image-viewer-shell">
        <div className="image-viewer-head">
          <div>
            <span className="kicker">Imagem protegida</span>
            <h2>{item?.marca || 'Armação'} {item?.ref || ''}</h2>
            <p>{item?.material || '—'} · {item?.cor || 'Sem cor'}</p>
          </div>

          <button type="button" className="plain-icon-btn" onClick={onClose}>
            <Icons.Close />
          </button>
        </div>

        <div className="image-viewer-body">
          <img src={imageUrl} alt={`${item?.marca || 'Armação'} ${item?.ref || ''}`} />
        </div>
      </div>
    </div>
  );
}

/* =============================================================
   Componente principal
============================================================= */

export default function Estoque({ onBack }) {
  const userLogado = useMemo(() => safeParseUser(), []);
  const empresaIdAtual = useMemo(() => getEmpresaIdFromUser(userLogado), [userLogado]);

  const [armacoes, setArmacoes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const [batchText, setBatchText] = useState('');
  const [selectedLojaCadastro, setSelectedLojaCadastro] = useState('');
  const [selectedEmpresaCadastroId, setSelectedEmpresaCadastroId] = useState('');

  const [novoItem, setNovoItem] = useState(() => criarItemVazio());
  const [novoImagemFile, setNovoImagemFile] = useState(null);
  const [novoImagemPreview, setNovoImagemPreview] = useState('');

  const [editingItem, setEditingItem] = useState(null);
  const [editingImagemFile, setEditingImagemFile] = useState(null);
  const [editingImagemPreview, setEditingImagemPreview] = useState('');

  const imagemBlobUrlsRef = useRef({});
  const tableScrollRef = useRef(null);
  const printModeRef = useRef(false);
  const [imagemBlobUrls, setImagemBlobUrls] = useState({});
  const [imagemAberta, setImagemAberta] = useState(null);
  const [visibleLimit, setVisibleLimit] = useState(INITIAL_VISIBLE_ITEMS);

  const [transferItem, setTransferItem] = useState(null);
  const [transferLoja, setTransferLoja] = useState('');
  const [transferEmpresaId, setTransferEmpresaId] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterLoja, setFilterLoja] = useState('');
  const [filterMarca, setFilterMarca] = useState('');
  const [filterMaterial, setFilterMaterial] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 450);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const limparImagemBlobUrls = useCallback(() => {
    Object.values(imagemBlobUrlsRef.current || {}).forEach((url) => {
      revogarPreviewBlob(url);
    });

    imagemBlobUrlsRef.current = {};
    setImagemBlobUrls({});
  }, []);

  const carregarImagemProtegida = useCallback(async (item) => {
    if (!item?._id || !item?.imagemUrl) return '';

    const endpoint = getImagemProtegidaEndpoint(item);

    if (!endpoint) return '';

    const response = await api.get(endpoint, {
      responseType: 'blob'
    });

    return URL.createObjectURL(response.data);
  }, []);

  const carregarImagensProtegidas = useCallback(async (itens = []) => {
    const itensComImagem = itens.filter((item) => (
      item?._id &&
      item?.imagemUrl &&
      !imagemBlobUrlsRef.current[item._id]
    ));

    if (!itensComImagem.length) return;

    const novasUrls = {};

    await Promise.allSettled(
      itensComImagem.map(async (item) => {
        const blobUrl = await carregarImagemProtegida(item);

        if (blobUrl) {
          novasUrls[item._id] = blobUrl;
        }
      })
    );

    if (!Object.keys(novasUrls).length) return;

    imagemBlobUrlsRef.current = {
      ...imagemBlobUrlsRef.current,
      ...novasUrls
    };

    setImagemBlobUrls({ ...imagemBlobUrlsRef.current });
  }, [carregarImagemProtegida]);

  useEffect(() => {
    return () => {
      limparImagemBlobUrls();
    };
  }, [limparImagemBlobUrls]);

  useEffect(() => {
    return () => {
      revogarPreviewBlob(novoImagemPreview);
      revogarPreviewBlob(editingImagemPreview);
    };
  }, [novoImagemPreview, editingImagemPreview]);

  const lojaOptions = useMemo(() => {
    const options = empresas.map((empresa) => ({
      id: normalizarId(empresa._id || empresa.id),
      value: getLojaValue(empresa),
      label: getEmpresaLabel(empresa),
      curto: getEmpresaCurta(empresa),
      empresa
    }));

    const lojasDoEstoque = Array.from(
      new Set(
        armacoes
          .map((item) => String(item.loja || '').trim())
          .filter(Boolean)
      )
    );

    lojasDoEstoque.forEach((loja) => {
      const exists = options.some((option) => normalizarTexto(option.value) === normalizarTexto(loja));

      if (!exists) {
        options.push({
          id: loja,
          value: loja,
          label: loja,
          curto: loja,
          empresa: null
        });
      }
    });

    return options;
  }, [empresas, armacoes]);

  useEffect(() => {
    if (!selectedLojaCadastro && lojaOptions.length) {
      const first = lojaOptions[0];

      setSelectedLojaCadastro(first.value);
      setSelectedEmpresaCadastroId(first.id || '');

      setNovoItem((prev) => ({
        ...prev,
        loja: prev.loja || first.value,
        empresaId: prev.empresaId || first.id || ''
      }));
    }
  }, [lojaOptions, selectedLojaCadastro]);

  const carregarEmpresas = useCallback(async () => {
    const response = await api.get('/empresas', {
      params: {
        ...montarParamsEmpresa(empresaIdAtual),
        comResumo: true
      }
    });

    const lista = resolverArrayApi(response.data, 'empresas')
      .filter((empresa) => empresa.ativo !== false);

    setEmpresas(lista);
  }, [empresaIdAtual]);

  const carregarArmacoes = useCallback(async () => {
    limparImagemBlobUrls();
    setVisibleLimit(INITIAL_VISIBLE_ITEMS);

    const response = await api.get('/api/armacoes');

    const lista = resolverArrayApi(response.data, 'armacoes');

    setArmacoes(lista);
  }, [limparImagemBlobUrls]);

  const carregarDados = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([
        carregarEmpresas(),
        carregarArmacoes()
      ]);
    } catch (error) {
      console.error('[Estoque] Erro ao carregar dados:', error);

      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  }, [carregarEmpresas, carregarArmacoes]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const armacoesFiltradasLocal = useMemo(() => {
    const termo = normalizarTexto(debouncedSearch);

    return armacoes.filter((item) => {
      const qtd = Number(item.qtd || 0);
      const empresa = encontrarEmpresaDoItem(item, empresas);

      if (filterStatus === 'esgotados' && qtd > 0) return false;
      if (filterStatus === 'baixo' && !(qtd > 0 && qtd <= LOW_STOCK_LIMIT)) return false;
      if (filterStatus === 'disponivel' && qtd <= LOW_STOCK_LIMIT) return false;

      if (filterLoja) {
        const lojaNormalizada = normalizarTexto(filterLoja);
        const combinaLoja =
          normalizarTexto(item.loja) === lojaNormalizada ||
          normalizarTexto(getEmpresaCodigo(empresa || {})) === lojaNormalizada ||
          normalizarTexto(empresa?.unidade) === lojaNormalizada ||
          normalizarTexto(empresa?.nome) === lojaNormalizada ||
          mesmoId(getItemEmpresaId(item), filterLoja) ||
          mesmoId(empresa?._id, filterLoja) ||
          mesmoId(empresa?.id, filterLoja);

        if (!combinaLoja) return false;
      }

      if (filterMarca && normalizarTexto(item.marca) !== normalizarTexto(filterMarca)) return false;
      if (filterMaterial && normalizarTexto(item.material) !== normalizarTexto(filterMaterial)) return false;

      if (termo) {
        const textoItem = normalizarTexto([
          item.marca,
          item.material,
          item.ref,
          item.aro,
          item.ponte,
          item.haste,
          item.cor,
          item.loja,
          empresa?.nome,
          empresa?.unidade,
          empresa?.codigoLoja
        ].filter(Boolean).join(' '));

        if (!textoItem.includes(termo)) return false;
      }

      return true;
    });
  }, [armacoes, empresas, debouncedSearch, filterLoja, filterMarca, filterMaterial, filterStatus]);

  const marcasDisponiveis = useMemo(() => {
    return Array.from(
      new Set(
        armacoes
          .map((item) => String(item.marca || '').trim().toUpperCase())
          .filter(Boolean)
      )
    ).sort();
  }, [armacoes]);

  const materiaisDisponiveis = useMemo(() => {
    return Array.from(
      new Set(
        armacoes
          .map((item) => String(item.material || '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [armacoes]);

  const stats = useMemo(() => {
    const itens = armacoesFiltradasLocal;
    const unidades = itens.reduce((acc, item) => acc + Number(item.qtd || 0), 0);
    const modelos = itens.length;
    const esgotados = itens.filter((item) => Number(item.qtd || 0) <= 0).length;
    const baixoEstoque = itens.filter((item) => {
      const qtd = Number(item.qtd || 0);
      return qtd > 0 && qtd <= LOW_STOCK_LIMIT;
    }).length;
    const valorEstoque = itens.reduce((acc, item) => acc + Number(item.qtd || 0) * Number(item.preco || 0), 0);

    const porLoja = lojaOptions.map((loja) => {
      const itensLoja = itens.filter((item) => {
        const empresa = encontrarEmpresaDoItem(item, empresas);

        return (
          String(item.loja || '') === loja.value ||
          mesmoId(getItemEmpresaId(item), loja.id) ||
          mesmoId(empresa?._id, loja.id) ||
          getEmpresaCodigo(empresa || {}) === loja.value
        );
      });

      return {
        ...loja,
        unidades: itensLoja.reduce((acc, item) => acc + Number(item.qtd || 0), 0),
        modelos: itensLoja.length,
        esgotados: itensLoja.filter((item) => Number(item.qtd || 0) <= 0).length,
        valor: itensLoja.reduce((acc, item) => acc + Number(item.qtd || 0) * Number(item.preco || 0), 0)
      };
    });

    return {
      unidades,
      modelos,
      esgotados,
      baixoEstoque,
      valorEstoque,
      porLoja
    };
  }, [armacoesFiltradasLocal, lojaOptions, empresas]);

  const armacoesVisiveis = useMemo(() => {
    return armacoesFiltradasLocal.slice(0, visibleLimit);
  }, [armacoesFiltradasLocal, visibleLimit]);

  const hasMoreItems = visibleLimit < armacoesFiltradasLocal.length;

  useEffect(() => {
    setVisibleLimit(INITIAL_VISIBLE_ITEMS);

    if (tableScrollRef.current) {
      tableScrollRef.current.scrollTop = 0;
    }
  }, [debouncedSearch, filterLoja, filterMarca, filterMaterial, filterStatus]);

  useEffect(() => {
    if (!printModeRef.current && !loading && armacoesVisiveis.length) {
      carregarImagensProtegidas(armacoesVisiveis);
    }
  }, [loading, armacoesVisiveis, carregarImagensProtegidas]);

  const carregarMaisItens = useCallback(() => {
    setVisibleLimit((prev) => Math.min(prev + LOAD_MORE_STEP, armacoesFiltradasLocal.length));
  }, [armacoesFiltradasLocal.length]);

  const handleTableScroll = useCallback((event) => {
    const el = event.currentTarget;
    const pertoDoFim = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_LOAD_OFFSET;

    if (pertoDoFim && visibleLimit < armacoesFiltradasLocal.length) {
      setVisibleLimit((prev) => Math.min(prev + LOAD_MORE_STEP, armacoesFiltradasLocal.length));
    }
  }, [visibleLimit, armacoesFiltradasLocal.length]);

  const limparFiltros = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setFilterLoja('');
    setFilterMarca('');
    setFilterMaterial('');
    setFilterStatus('todos');
  };

  const atualizarPreviewNovaImagem = (file) => {
    setNovoImagemFile(file || null);
    setNovoImagemPreview((prev) => {
      revogarPreviewBlob(prev);
      return file ? URL.createObjectURL(file) : '';
    });
  };

  const atualizarPreviewEdicaoImagem = (file) => {
    setEditingImagemFile(file || null);
    setEditingImagemPreview((prev) => {
      revogarPreviewBlob(prev);
      return file ? URL.createObjectURL(file) : '';
    });
  };

  const abrirCadastroIndividual = () => {
    const first = lojaOptions[0];

    setNovoItem(criarItemVazio(
      first?.value || selectedLojaCadastro || '',
      first?.id || selectedEmpresaCadastroId || ''
    ));

    atualizarPreviewNovaImagem(null);
    setIsCreateModalOpen(true);
  };

  const handleNovoLojaChange = (value) => {
    const option = lojaOptions.find((loja) => loja.value === value);

    setNovoItem((prev) => ({
      ...prev,
      loja: value,
      empresaId: option?.id || prev?.empresaId || ''
    }));
  };

  const criarItemIndividual = async () => {
    const item = normalizarItemParaSalvar(novoItem);

    if (!item.marca || !item.material || !item.ref || !item.cor) {
      alert('Marca, material, referência e cor são obrigatórios.');
      return;
    }

    setSaving(true);

    try {
      await api.post('/api/armacoes', montarFormDataArmacao(item, novoImagemFile), {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setIsCreateModalOpen(false);
      setNovoItem(criarItemVazio(selectedLojaCadastro, selectedEmpresaCadastroId));
      atualizarPreviewNovaImagem(null);

      await carregarArmacoes();
    } catch (error) {
      console.error('[Estoque] Erro ao criar armação:', error);
      alert(getMensagemErroApi(error, 'Erro ao criar armação.'));
    } finally {
      setSaving(false);
    }
  };

  const removerImagemNova = () => {
    atualizarPreviewNovaImagem(null);
  };

  const handleEditClick = async (item) => {
    const empresa = encontrarEmpresaDoItem(item, empresas);

    setEditingItem({
      ...item,
      empresaId: getItemEmpresaId(item) || normalizarId(empresa?._id || empresa?.id || '')
    });

    setEditingImagemFile(null);
    setEditingImagemPreview((prev) => {
      revogarPreviewBlob(prev);
      return '';
    });

    setIsEditModalOpen(true);

    if (item?.imagemUrl) {
      try {
        const blobUrl = await carregarImagemProtegida(item);

        if (blobUrl) {
          setEditingImagemPreview((prev) => {
            revogarPreviewBlob(prev);
            return blobUrl;
          });
        }
      } catch (error) {
        console.error('[Estoque] Erro ao carregar imagem da edição:', error);
      }
    }
  };

  const salvarEdicao = async () => {
    if (!editingItem?._id) return;

    setSaving(true);

    try {
      if (editingImagemFile) {
        await api.put(
          `/api/armacoes/${editingItem._id}`,
          montarFormDataArmacao(editingItem, editingImagemFile),
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        await api.put(`/api/armacoes/${editingItem._id}`, normalizarItemParaSalvar(editingItem));
      }

      setIsEditModalOpen(false);
      setEditingItem(null);
      setEditingImagemFile(null);
      setEditingImagemPreview((prev) => {
        revogarPreviewBlob(prev);
        return '';
      });

      await carregarArmacoes();
    } catch (error) {
      console.error('[Estoque] Erro ao salvar edição:', error);
      alert(getMensagemErroApi(error, 'Erro ao salvar edição.'));
    } finally {
      setSaving(false);
    }
  };

  const removerImagemEdicao = async () => {
    if (!editingItem?._id) {
      atualizarPreviewEdicaoImagem(null);
      return;
    }

    if (editingImagemFile) {
      setEditingImagemFile(null);

      setEditingImagemPreview((prev) => {
        revogarPreviewBlob(prev);
        return '';
      });

      if (editingItem?.imagemUrl) {
        try {
          const blobUrl = await carregarImagemProtegida(editingItem);

          if (blobUrl) {
            setEditingImagemPreview(blobUrl);
          }
        } catch (error) {
          console.error('[Estoque] Erro ao restaurar imagem atual:', error);
        }
      }

      return;
    }

    if (!editingItem.imagemUrl) return;

    const confirmar = window.confirm('Remover a imagem desta armação?');

    if (!confirmar) return;

    setSaving(true);

    try {
      await api.delete(`/api/armacoes/${editingItem._id}/imagem`);

      setEditingItem((prev) => ({
        ...prev,
        imagemUrl: ''
      }));

      setEditingImagemPreview('');
      await carregarArmacoes();
    } catch (error) {
      console.error('[Estoque] Erro ao remover imagem:', error);
      alert(getMensagemErroApi(error, 'Erro ao remover imagem.'));
    } finally {
      setSaving(false);
    }
  };

  const venderPeca = async (item) => {
    if (!item?._id) return;

    if (Number(item.qtd || 0) <= 0) {
      alert('Essa peça já está esgotada.');
      return;
    }

    try {
      await api.patch(`/api/armacoes/vender/${item._id}`);
      await carregarArmacoes();
    } catch (error) {
      console.error('[Estoque] Erro ao registrar venda:', error);
      alert(error.response?.data?.error || error.response?.data?.erro || 'Erro ao registrar venda.');
    }
  };

  const alterarQtd = async (item, delta) => {
    if (!item?._id) return;

    if (delta < 0 && Number(item.qtd || 0) <= 0) return;

    try {
      await api.patch(`/api/armacoes/quantidade/${item._id}`, { delta });
      await carregarArmacoes();
    } catch (error) {
      console.error('[Estoque] Erro ao alterar quantidade:', error);
      alert(error.response?.data?.error || error.response?.data?.erro || 'Erro ao alterar quantidade.');
    }
  };

  const checarArmacao = async (item) => {
    if (!item?._id) return;

    const proximoStatus = !Boolean(item.checado);

    try {
      const response = await api.patch(`/api/armacoes/checar/${item._id}`, {
        checado: proximoStatus
      });

      const armacaoAtualizada = response.data?.armacao || response.data;

      setArmacoes((prev) =>
        prev.map((armacao) => (
          normalizarId(armacao._id) === normalizarId(item._id)
            ? {
                ...armacao,
                ...armacaoAtualizada,
                checado: Boolean(armacaoAtualizada?.checado ?? proximoStatus)
              }
            : armacao
        ))
      );
    } catch (error) {
      console.error('[Estoque] Erro ao checar armação:', error);
      alert(getMensagemErroApi(error, 'Erro ao checar armação.'));
    }
  };

  const abrirTransferencia = (item) => {
    const empresaAtual = encontrarEmpresaDoItem(item, empresas);
    const atualId = getItemEmpresaId(item) || normalizarId(empresaAtual?._id || empresaAtual?.id || '');

    setTransferItem(item);
    setTransferLoja(item.loja || getEmpresaCodigo(empresaAtual || '') || '');
    setTransferEmpresaId(atualId);
    setIsTransferModalOpen(true);
  };

  const salvarTransferencia = async () => {
    if (!transferItem?._id) return;

    setSaving(true);

    try {
      await api.patch(`/api/armacoes/transferir/${transferItem._id}`, {
        loja: transferLoja,
        empresaId: transferEmpresaId
      });

      setIsTransferModalOpen(false);
      setTransferItem(null);

      await carregarArmacoes();
    } catch (error) {
      console.error('[Estoque] Erro na transferência:', error);
      alert(error.response?.data?.error || error.response?.data?.erro || 'Erro na transferência de loja.');
    } finally {
      setSaving(false);
    }
  };

  const excluirRegistro = async (item) => {
    if (!item?._id) return;

    const confirm = window.confirm(`Remover ${item.marca || ''} ${item.ref || ''} do estoque?`);

    if (!confirm) return;

    try {
      await api.delete(`/api/armacoes/${item._id}`);
      await carregarArmacoes();
    } catch (error) {
      console.error('[Estoque] Erro ao excluir:', error);
      alert(error.response?.data?.error || error.response?.data?.erro || 'Erro ao excluir registro.');
    }
  };

  const processBatch = async () => {
    if (!batchText.trim()) return;

    const linhas = batchText
      .split('\n')
      .map((linha) => linha.trim())
      .filter(Boolean);

    const novosItens = linhas
      .map((linha) => {
        const partes = linha.split(/\t|;|,/).map((p) => p.trim()).filter(Boolean);
        const usandoSeparador = partes.length >= 3;
        const tokens = usandoSeparador ? partes : linha.split(/\s+/).filter(Boolean);

        if (tokens.length < 3) return null;

        const marca = tokens[0] || '';
        const material = tokens[1] || '';
        const ref = tokens[2] || '';
        const aro = tokens[3] || '';
        const ponte = tokens[4] || '';
        const haste = tokens[5] || '';
        const cor = tokens.slice(6).join(' ') || tokens[6] || '';

        return normalizarItemParaSalvar({
          loja: selectedLojaCadastro,
          empresaId: selectedEmpresaCadastroId,
          marca,
          material,
          ref,
          aro,
          ponte,
          haste,
          cor,
          qtd: 1,
          preco: 0
        });
      })
      .filter(Boolean);

    if (!novosItens.length) {
      alert('Nenhuma linha válida encontrada.');
      return;
    }

    setSaving(true);

    try {
      await api.post('/api/armacoes/batch', novosItens);

      setBatchText('');
      setIsModalOpen(false);

      await carregarArmacoes();
    } catch (error) {
      console.error('[Estoque] Erro ao salvar lote:', error);
      alert(error.response?.data?.error || error.response?.data?.erro || 'Erro ao salvar entrada de estoque.');
    } finally {
      setSaving(false);
    }
  };

  const handleLojaCadastroChange = (value) => {
    const option = lojaOptions.find((loja) => loja.value === value);

    setSelectedLojaCadastro(value);
    setSelectedEmpresaCadastroId(option?.id || '');

    setNovoItem((prev) => ({
      ...prev,
      loja: value,
      empresaId: option?.id || prev?.empresaId || ''
    }));
  };

  const handleEditLojaChange = (value) => {
    const option = lojaOptions.find((loja) => loja.value === value);

    setEditingItem((prev) => ({
      ...prev,
      loja: value,
      empresaId: option?.id || prev?.empresaId || ''
    }));
  };

  const handleTransferLojaChange = (value) => {
    const option = lojaOptions.find((loja) => loja.value === value);

    setTransferLoja(value);
    setTransferEmpresaId(option?.id || '');
  };

  const abrirImagemArmacao = async (item) => {
    if (!item?._id || !item?.imagemUrl) return;

    let blobUrl = imagemBlobUrlsRef.current[item._id];

    let temporaria = false;

    if (!blobUrl) {
      try {
        blobUrl = await carregarImagemProtegida(item);
        temporaria = true;
      } catch (error) {
        console.error('[Estoque] Erro ao abrir imagem protegida:', error);
        alert(getMensagemErroApi(error, 'Erro ao abrir imagem da armação.'));
        return;
      }
    }

    if (!blobUrl) return;

    setImagemAberta({
      item,
      imageUrl: blobUrl,
      temporaria
    });
  };

  const fecharImagemAberta = () => {
    setImagemAberta((prev) => {
      if (prev?.temporaria) {
        revogarPreviewBlob(prev.imageUrl);
      }

      return null;
    });
  };

  const handlePrint = () => {
    const previousLimit = visibleLimit;

    printModeRef.current = true;
    setVisibleLimit(Math.max(armacoesFiltradasLocal.length, INITIAL_VISIBLE_ITEMS));

    window.setTimeout(() => {
      window.print();

      window.setTimeout(() => {
        printModeRef.current = false;
        setVisibleLimit(previousLimit);
      }, 300);
    }, 80);
  };

  return (
    <div className="stock-page">
      <GlobalEstoqueStyles />

      <header className="stock-top no-print">
        <div className="stock-top-left">
          {onBack ? (
            <button type="button" className="plain-btn" onClick={onBack}>
              <Icons.ArrowLeft />
              Voltar
            </button>
          ) : (
            <button type="button" className="plain-btn" onClick={() => { window.location.href = '/home'; }}>
              <Icons.ArrowLeft />
              Dashboard
            </button>
          )}

          <div>
            <span className="kicker">Lumix Óticas</span>
            <h1>Estoque de armações</h1>
            <p>Controle rápido com carregamento progressivo de 10 em 10 peças.</p>
          </div>
        </div>

        <div className="top-actions">
          <button type="button" className="plain-icon-btn" onClick={carregarDados} title="Atualizar">
            <Icons.Refresh />
          </button>

          <button type="button" className="plain-btn" onClick={handlePrint}>
            <Icons.Printer />
            Imprimir
          </button>

          <button type="button" className="plain-btn" onClick={() => setIsModalOpen(true)}>
            <Icons.Plus />
            Entrada rápida
          </button>

          <button type="button" className="primary-btn" onClick={abrirCadastroIndividual}>
            <Icons.Image />
            Nova armação
          </button>
        </div>
      </header>

      <main className="stock-main">
        <div className="print-header">
          <div>
            <h1>Relatório de Estoque</h1>
            <p>Gerado em: {getDataAtualBR()}</p>
          </div>

          <div>
            <p><strong>Modelos:</strong> {stats.modelos}</p>
            <p><strong>Peças:</strong> {stats.unidades}</p>
          </div>
        </div>

        <section className="kpi-strip no-print">
          <KpiCell
            icon={<Icons.Box />}
            label="Estoque total"
            value={stats.unidades}
            help={`${stats.modelos} SKU(s)`}
            tone="blue"
          />

          <KpiCell
            icon={<Icons.Alert />}
            label="Baixo estoque"
            value={stats.baixoEstoque}
            help={`Até ${LOW_STOCK_LIMIT} peça(s)`}
            tone="orange"
          />

          <KpiCell
            icon={<Icons.Alert />}
            label="Esgotados"
            value={stats.esgotados}
            help="Modelos zerados"
            tone="red"
          />

          <KpiCell
            icon={<Icons.Dollar />}
            label="Valor estimado"
            value={formatCurrency(stats.valorEstoque)}
            help="qtd × preço"
            tone="green"
          />
        </section>

        <section className="store-strip no-print">
          {stats.porLoja.map((loja) => (
            <div key={loja.id || loja.value} className="store-mini">
              <span>{loja.curto}</span>
              <strong>{loja.unidades}</strong>
              <small>{loja.modelos} SKU · {loja.esgotados} zerado(s)</small>
            </div>
          ))}
        </section>

        <section className="filters-line no-print">
          <div className="search-box">
            <Icons.Search />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar referência, marca, cor..."
            />
          </div>

          <select value={filterLoja} onChange={(event) => setFilterLoja(event.target.value)}>
            <option value="">Todas as óticas</option>
            {lojaOptions.map((loja) => (
              <option key={loja.id || loja.value} value={loja.value}>
                {loja.label}
              </option>
            ))}
          </select>

          <select value={filterMarca} onChange={(event) => setFilterMarca(event.target.value)}>
            <option value="">Todas as marcas</option>
            {marcasDisponiveis.map((marca) => (
              <option key={marca} value={marca}>
                {marca}
              </option>
            ))}
          </select>

          <select value={filterMaterial} onChange={(event) => setFilterMaterial(event.target.value)}>
            <option value="">Todos os materiais</option>
            {materiaisDisponiveis.map((material) => (
              <option key={material} value={material}>
                {material}
              </option>
            ))}
          </select>

          <select value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>
            <option value="todos">Todos os status</option>
            <option value="disponivel">Disponível</option>
            <option value="baixo">Baixo estoque</option>
            <option value="esgotados">Esgotados</option>
          </select>

          {(searchTerm || filterLoja || filterMarca || filterMaterial || filterStatus !== 'todos') ? (
            <button type="button" className="plain-btn danger" onClick={limparFiltros}>
              Limpar
            </button>
          ) : null}
        </section>

        <section className="table-panel">
          {loading ? (
            <LoadingPanel />
          ) : (
            <>
              <div
                ref={tableScrollRef}
                className="table-scroll"
                onScroll={handleTableScroll}
              >
                <table className="stock-table">
                <thead>
                  <tr>
                    <th className="center no-print">Foto</th>
                    <th>Produto</th>
                    <th>Características</th>
                    <th>Localização</th>
                    <th className="center">Qtd</th>
                    <th className="right">Preço</th>
                    <th className="right no-print">Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {armacoesFiltradasLocal.length ? (
                    armacoesVisiveis.map((item) => {
                      const qtd = Number(item.qtd || 0);
                      const isZerado = qtd <= 0;
                      const isBaixo = qtd > 0 && qtd <= LOW_STOCK_LIMIT;
                      const isChecado = Boolean(item.checado);
                      const rowClassName = [
                        isZerado ? 'row-empty' : isBaixo ? 'row-low' : '',
                        isChecado ? 'row-checked' : ''
                      ].filter(Boolean).join(' ');

                      return (
                        <tr key={item._id} className={rowClassName}>
                          <td className="center no-print">
                            {item.imagemUrl && imagemBlobUrls[item._id] ? (
                              <button
                                type="button"
                                className="thumb-cell has-image"
                                onClick={() => abrirImagemArmacao(item)}
                                title="Clique para ampliar a imagem"
                              >
                                <img
                                  src={imagemBlobUrls[item._id]}
                                  alt={`${item.marca || 'Armação'} ${item.ref || ''}`}
                                />
                              </button>
                            ) : (
                              <div className="thumb-cell empty" title={item.imagemUrl ? 'A imagem será carregada quando a peça aparecer na lista' : 'Sem imagem'}>
                                <span><Icons.Image /></span>
                              </div>
                            )}
                          </td>

                          <td>
                            <div className="product-cell">
                              <strong>{item.marca || 'Sem marca'}</strong>
                              <span>{item.ref || 'Sem ref.'}</span>
                              {isChecado ? (
                                <small className="verified-badge no-print">
                                  <Icons.Check />
                                  Verificada
                                </small>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="meta-line">
                              <span>{item.material || '—'}</span>
                              <i />
                              <span>{item.cor || 'Sem cor'}</span>
                              {(item.aro || item.ponte || item.haste) ? (
                                <>
                                  <i />
                                  <em>{item.aro || '—'}□{item.ponte || '—'}-{item.haste || '—'}</em>
                                </>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <LojaBadge item={item} empresas={empresas} />
                          </td>

                          <td className="center">
                            <div className="qty-control no-print">
                              <button type="button" onClick={() => alterarQtd(item, -1)} disabled={qtd <= 0}>-</button>
                              <strong>{qtd}</strong>
                              <button type="button" onClick={() => alterarQtd(item, 1)}>+</button>
                            </div>

                            <span className="qty-print">{qtd}</span>
                          </td>

                          <td className="right">
                            <span className="price-cell">{formatCurrency(item.preco)}</span>
                          </td>

                          <td className="right no-print">
                            <div className="actions-cell">
                              <button
                                type="button"
                                className={`check-action ${isChecado ? 'checked' : ''}`}
                                title={isChecado ? 'Desmarcar verificação' : 'Marcar como verificada'}
                                onClick={() => checarArmacao(item)}
                              >
                                <Icons.Check />
                              </button>

                              <button type="button" title="Registrar venda" onClick={() => venderPeca(item)}>
                                <Icons.Dollar />
                              </button>

                              <button type="button" title="Transferir" onClick={() => abrirTransferencia(item)}>
                                <Icons.Transfer />
                              </button>

                              <button type="button" title="Editar" onClick={() => handleEditClick(item)}>
                                <Icons.Edit />
                              </button>

                              <button type="button" className="danger" title="Excluir" onClick={() => excluirRegistro(item)}>
                                <Icons.Trash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="empty-table">
                        Nenhum item encontrado com estes filtros.
                      </td>
                    </tr>
                  )}
                </tbody>
                </table>
              </div>

              <div className="stock-pagination no-print">
                <span>
                  Mostrando <strong>{Math.min(armacoesVisiveis.length, armacoesFiltradasLocal.length)}</strong> de <strong>{armacoesFiltradasLocal.length}</strong> peça(s)
                </span>

                {hasMoreItems ? (
                  <button type="button" className="plain-btn" onClick={carregarMaisItens}>
                    Carregar mais 10
                  </button>
                ) : armacoesFiltradasLocal.length ? (
                  <small>Tudo carregado</small>
                ) : null}
              </div>
            </>
          )}
        </section>
      </main>

      {isCreateModalOpen ? (
        <Modal
          title="Nova armação"
          subtitle="Cadastre uma peça individual com imagem comprimida no backend."
          onClose={() => setIsCreateModalOpen(false)}
          width={760}
        >
          <div className="modal-body">
            <ImagePicker
              preview={novoImagemPreview}
              hasImage={Boolean(novoImagemPreview)}
              onChange={atualizarPreviewNovaImagem}
              onRemove={removerImagemNova}
              onOpen={() => setImagemAberta({ item: novoItem, imageUrl: novoImagemPreview })}
            />

            <div className="edit-grid">
              <SelectField label="Ótica" value={novoItem.loja || ''} onChange={handleNovoLojaChange}>
                <option value="">Selecione</option>
                {lojaOptions.map((loja) => (
                  <option key={loja.id || loja.value} value={loja.value}>
                    {loja.label}
                  </option>
                ))}
              </SelectField>

              <Field label="Marca" value={novoItem.marca} onChange={(value) => setNovoItem((prev) => ({ ...prev, marca: value.toUpperCase() }))} />
              <Field label="Material" value={novoItem.material} onChange={(value) => setNovoItem((prev) => ({ ...prev, material: value }))} />
              <Field label="Referência" value={novoItem.ref} onChange={(value) => setNovoItem((prev) => ({ ...prev, ref: value.toUpperCase() }))} />
              <Field label="Cor" value={novoItem.cor} onChange={(value) => setNovoItem((prev) => ({ ...prev, cor: value.toUpperCase() }))} />
              <Field label="Aro" value={novoItem.aro} onChange={(value) => setNovoItem((prev) => ({ ...prev, aro: value }))} />
              <Field label="Ponte" value={novoItem.ponte} onChange={(value) => setNovoItem((prev) => ({ ...prev, ponte: value }))} />
              <Field label="Haste" value={novoItem.haste} onChange={(value) => setNovoItem((prev) => ({ ...prev, haste: value }))} />
              <Field label="Quantidade" type="number" value={novoItem.qtd} onChange={(value) => setNovoItem((prev) => ({ ...prev, qtd: value }))} />
              <Field label="Preço" value={novoItem.preco} inputMode="decimal" onChange={(value) => setNovoItem((prev) => ({ ...prev, preco: value }))} />
            </div>
          </div>

          <div className="modal-foot">
            <button type="button" className="plain-btn" onClick={() => setIsCreateModalOpen(false)} disabled={saving}>
              Cancelar
            </button>

            <button type="button" className="primary-btn" onClick={criarItemIndividual} disabled={saving}>
              <Icons.Save />
              {saving ? 'Salvando...' : 'Salvar armação'}
            </button>
          </div>
        </Modal>
      ) : null}

      {isModalOpen ? (
        <Modal
          title="Entrada rápida em lote"
          subtitle="Cole linhas da planilha e selecione a unidade de destino."
          onClose={() => setIsModalOpen(false)}
          width={680}
        >
          <div className="modal-body">
            <div className="soft-note">
              <strong>Padrão recomendado:</strong>
              <span>MARCA MATERIAL REFERÊNCIA ARO PONTE HASTE COR</span>
              <small>Ex.: RAYBAN ACETATO RB4105 54 18 140 PRETO</small>
            </div>

            <SelectField label="Ótica de destino" value={selectedLojaCadastro} onChange={handleLojaCadastroChange}>
              {lojaOptions.map((loja) => (
                <option key={loja.id || loja.value} value={loja.value}>
                  {loja.label}
                </option>
              ))}
            </SelectField>

            <label className="form-field">
              <span>Dados da entrada</span>
              <textarea
                value={batchText}
                onChange={(event) => setBatchText(event.target.value)}
                placeholder="Cole as linhas aqui..."
                rows={8}
              />
            </label>
          </div>

          <div className="modal-foot">
            <button type="button" className="plain-btn" onClick={() => setIsModalOpen(false)} disabled={saving}>
              Cancelar
            </button>

            <button type="button" className="primary-btn" onClick={processBatch} disabled={saving || !batchText.trim()}>
              <Icons.Save />
              {saving ? 'Salvando...' : 'Processar e salvar'}
            </button>
          </div>
        </Modal>
      ) : null}

      {isEditModalOpen && editingItem ? (
        <Modal
          title="Editar armação"
          subtitle={`${editingItem.marca || ''} ${editingItem.ref || ''}`}
          onClose={() => setIsEditModalOpen(false)}
          width={720}
        >
          <div className="modal-body">
            <ImagePicker
              preview={editingImagemPreview}
              hasImage={Boolean(editingItem.imagemUrl || editingImagemPreview)}
              onChange={atualizarPreviewEdicaoImagem}
              onRemove={removerImagemEdicao}
              onOpen={() => setImagemAberta({ item: editingItem, imageUrl: editingImagemPreview })}
            />

            <div className="edit-grid">
              <SelectField label="Ótica" value={editingItem.loja || ''} onChange={handleEditLojaChange}>
                <option value="">Selecione</option>
                {lojaOptions.map((loja) => (
                  <option key={loja.id || loja.value} value={loja.value}>
                    {loja.label}
                  </option>
                ))}
              </SelectField>

              <Field label="Marca" value={editingItem.marca} onChange={(value) => setEditingItem((prev) => ({ ...prev, marca: value.toUpperCase() }))} />
              <Field label="Material" value={editingItem.material} onChange={(value) => setEditingItem((prev) => ({ ...prev, material: value }))} />
              <Field label="Referência" value={editingItem.ref} onChange={(value) => setEditingItem((prev) => ({ ...prev, ref: value }))} />
              <Field label="Cor" value={editingItem.cor} onChange={(value) => setEditingItem((prev) => ({ ...prev, cor: value }))} />
              <Field label="Aro" value={editingItem.aro} onChange={(value) => setEditingItem((prev) => ({ ...prev, aro: value }))} />
              <Field label="Ponte" value={editingItem.ponte} onChange={(value) => setEditingItem((prev) => ({ ...prev, ponte: value }))} />
              <Field label="Haste" value={editingItem.haste} onChange={(value) => setEditingItem((prev) => ({ ...prev, haste: value }))} />
              <Field label="Quantidade" type="number" value={editingItem.qtd} onChange={(value) => setEditingItem((prev) => ({ ...prev, qtd: value }))} />
              <Field label="Preço" value={editingItem.preco} inputMode="decimal" onChange={(value) => setEditingItem((prev) => ({ ...prev, preco: value }))} />
            </div>
          </div>

          <div className="modal-foot">
            <button type="button" className="plain-btn" onClick={() => setIsEditModalOpen(false)} disabled={saving}>
              Cancelar
            </button>

            <button type="button" className="primary-btn" onClick={salvarEdicao} disabled={saving}>
              <Icons.Save />
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </div>
        </Modal>
      ) : null}

      {isTransferModalOpen && transferItem ? (
        <Modal
          title="Transferir armação"
          subtitle={`${transferItem.marca || ''} ${transferItem.ref || ''}`}
          onClose={() => setIsTransferModalOpen(false)}
          width={520}
        >
          <div className="modal-body">
            <SelectField label="Enviar para" value={transferLoja} onChange={handleTransferLojaChange}>
              <option value="">Selecione</option>
              {lojaOptions.map((loja) => (
                <option key={loja.id || loja.value} value={loja.value}>
                  {loja.label}
                </option>
              ))}
            </SelectField>

            <div className="soft-note">
              <span>A transferência atualiza a unidade da peça. Caso seu backend antigo ainda faça alternância automática, ele pode ignorar o destino enviado.</span>
            </div>
          </div>

          <div className="modal-foot">
            <button type="button" className="plain-btn" onClick={() => setIsTransferModalOpen(false)} disabled={saving}>
              Cancelar
            </button>

            <button type="button" className="primary-btn" onClick={salvarTransferencia} disabled={saving || !transferLoja}>
              <Icons.Transfer />
              {saving ? 'Transferindo...' : 'Transferir'}
            </button>
          </div>
        </Modal>
      ) : null}

      {imagemAberta?.imageUrl ? (
        <ImageViewerModal
          item={imagemAberta.item}
          imageUrl={imagemAberta.imageUrl}
          onClose={fecharImagemAberta}
        />
      ) : null}
    </div>
  );
}

/* =============================================================
   CSS
============================================================= */

function GlobalEstoqueStyles() {
  return (
    <style>{`
      :root {
        --stock-bg: #f8fafd;
        --stock-surface: #ffffff;
        --stock-surface-2: #f1f3f4;
        --stock-text: #202124;
        --stock-muted: #5f6368;
        --stock-soft: #80868b;
        --stock-border: #dadce0;
        --stock-border-soft: #edf0f3;
        --stock-blue: #1a73e8;
        --stock-green: #188038;
        --stock-red: #d93025;
        --stock-orange: #b06000;
        --stock-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Arial, sans-serif;
      }

      * {
        box-sizing: border-box;
      }

      .stock-page {
        min-height: 100vh;
        color: var(--stock-text);
        background: var(--stock-bg);
        font-family: var(--stock-font);
        -webkit-font-smoothing: antialiased;
        text-rendering: geometricPrecision;
      }

      .stock-top {
        padding: 18px;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 14px;
      }

      .stock-top-left {
        display: flex;
        align-items: flex-start;
        gap: 10px;
      }

      .kicker {
        display: block;
        margin-bottom: 2px;
        color: var(--stock-muted);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .04em;
        text-transform: uppercase;
      }

      .stock-top h1 {
        margin: 0;
        color: var(--stock-text);
        font-size: 26px;
        line-height: 1.1;
        font-weight: 650;
        letter-spacing: -.035em;
      }

      .stock-top p {
        margin: 4px 0 0;
        color: var(--stock-muted);
        font-size: 13px;
      }

      .top-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        justify-content: flex-end;
      }

      .stock-main {
        padding: 0 18px 18px;
      }

      .plain-btn,
      .plain-icon-btn,
      .primary-btn {
        height: 34px;
        border: 1px solid var(--stock-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        background: #fff;
        font-size: 12px;
        font-weight: 650;
        font-family: inherit;
        cursor: pointer;
      }

      .plain-btn {
        padding: 0 11px;
        color: var(--stock-muted);
      }

      .plain-btn.danger {
        color: var(--stock-red);
        border-color: #fecaca;
        background: #fff;
      }

      .plain-icon-btn {
        width: 34px;
        padding: 0;
        color: var(--stock-muted);
        flex-shrink: 0;
      }

      .plain-btn:hover,
      .plain-icon-btn:hover {
        color: var(--stock-text);
        background: var(--stock-surface-2);
      }

      .primary-btn {
        padding: 0 14px;
        border-color: #1557b0;
        color: #fff;
        background: var(--stock-blue);
      }

      .primary-btn:disabled,
      .plain-btn:disabled {
        opacity: .65;
        cursor: not-allowed;
      }

      .kpi-strip {
        margin-bottom: 10px;
        border: 1px solid var(--stock-border);
        border-radius: 13px;
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        overflow: hidden;
        background: #fff;
      }

      .kpi-cell {
        min-height: 82px;
        padding: 12px;
        border-right: 1px solid var(--stock-border-soft);
        display: grid;
        grid-template-columns: 28px 1fr;
        grid-template-rows: auto auto auto;
        column-gap: 8px;
        align-content: center;
        background: #fff;
      }

      .kpi-cell:last-child {
        border-right: 0;
      }

      .kpi-icon {
        grid-row: 1 / span 3;
        width: 28px;
        height: 28px;
        border: 1px solid var(--stock-border);
        border-radius: 9px;
        display: grid;
        place-items: center;
        color: var(--stock-blue);
        background: #f8fafd;
      }

      .kpi-cell.blue .kpi-icon {
        color: var(--stock-blue);
        background: #eef5ff;
        border-color: #d2e3fc;
      }

      .kpi-cell.green .kpi-icon {
        color: var(--stock-green);
        background: #f1f8f4;
        border-color: #cce8d5;
      }

      .kpi-cell.orange .kpi-icon {
        color: var(--stock-orange);
        background: #fff7ed;
        border-color: #fed7aa;
      }

      .kpi-cell.red .kpi-icon {
        color: var(--stock-red);
        background: #fef2f2;
        border-color: #fecaca;
      }

      .kpi-cell span {
        color: var(--stock-muted);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .02em;
        text-transform: uppercase;
      }

      .kpi-cell strong {
        margin-top: 3px;
        color: var(--stock-text);
        font-size: 18px;
        line-height: 1.15;
        font-weight: 700;
        letter-spacing: -.025em;
      }

      .kpi-cell small {
        margin-top: 3px;
        color: var(--stock-soft);
        font-size: 11px;
        font-weight: 500;
      }

      .store-strip {
        margin-bottom: 10px;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
        gap: 8px;
      }

      .store-mini {
        min-height: 70px;
        padding: 10px;
        border: 1px solid var(--stock-border);
        border-radius: 13px;
        display: grid;
        gap: 2px;
        align-content: center;
        background: #fff;
      }

      .store-mini span {
        color: var(--stock-muted);
        font-size: 11px;
        font-weight: 750;
        text-transform: uppercase;
      }

      .store-mini strong {
        color: var(--stock-text);
        font-size: 20px;
        font-weight: 750;
      }

      .store-mini small {
        color: var(--stock-soft);
        font-size: 11px;
      }

      .filters-line {
        margin-bottom: 10px;
        padding: 9px;
        border: 1px solid var(--stock-border);
        border-radius: 13px;
        display: grid;
        grid-template-columns: minmax(220px, 1fr) minmax(160px, 220px) minmax(130px, 180px) minmax(130px, 180px) minmax(130px, 170px) auto;
        gap: 8px;
        background: #fff;
      }

      .search-box {
        height: 34px;
        padding: 0 10px;
        border: 1px solid var(--stock-border);
        border-radius: 999px;
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--stock-muted);
        background: #fff;
      }

      .search-box input {
        width: 100%;
        border: 0;
        outline: 0;
        color: var(--stock-text);
        background: transparent;
        font-size: 12.5px;
        font-family: inherit;
      }

      .filters-line select,
      .form-field select,
      .form-field input,
      .form-field textarea {
        height: 34px;
        padding: 0 10px;
        border: 1px solid var(--stock-border);
        border-radius: 10px;
        outline: 0;
        color: var(--stock-text);
        background: #fff;
        font-size: 12px;
        font-family: inherit;
      }

      .form-field textarea {
        min-height: 150px;
        padding: 10px;
        resize: vertical;
        line-height: 1.45;
      }

      .filters-line select {
        border-radius: 999px;
      }

      .search-box:focus-within,
      .filters-line select:focus,
      .form-field select:focus,
      .form-field input:focus,
      .form-field textarea:focus {
        border-color: var(--stock-blue);
        box-shadow: 0 0 0 3px rgba(26, 115, 232, .10);
      }

      .table-panel {
        border: 1px solid var(--stock-border);
        border-radius: 13px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 8px 28px rgba(60, 64, 67, .05);
      }

      .stock-pagination {
        min-height: 48px;
        padding: 9px 12px;
        border-top: 1px solid var(--stock-border-soft);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        color: var(--stock-muted);
        background: linear-gradient(180deg, #fff, #fbfcfe);
        font-size: 12px;
      }

      .stock-pagination strong {
        color: var(--stock-text);
        font-weight: 800;
      }

      .stock-pagination small {
        color: var(--stock-green);
        font-size: 11px;
        font-weight: 750;
        text-transform: uppercase;
        letter-spacing: .03em;
      }

      .table-scroll {
        overflow: auto;
        max-height: calc(100vh - 306px);
      }

      .table-scroll::-webkit-scrollbar {
        width: 7px;
        height: 7px;
      }

      .table-scroll::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: rgba(95, 99, 104, .28);
      }

      .stock-table {
        width: 100%;
        min-width: 880px;
        border-collapse: collapse;
      }

      .stock-table th {
        position: sticky;
        top: 0;
        z-index: 2;
        height: 38px;
        padding: 0 12px;
        border-bottom: 1px solid var(--stock-border);
        color: var(--stock-muted);
        background: #f8fafd;
        font-size: 10.5px;
        font-weight: 800;
        letter-spacing: .04em;
        text-align: left;
        text-transform: uppercase;
        white-space: nowrap;
      }

      .stock-table td {
        height: 52px;
        padding: 0 12px;
        border-bottom: 1px solid var(--stock-border-soft);
        color: var(--stock-text);
        background: #fff;
        font-size: 12.5px;
        vertical-align: middle;
        white-space: nowrap;
        transition: background .16s ease;
      }

      .stock-table tbody tr {
        animation: rowIn .18s ease both;
      }

      @keyframes rowIn {
        from { opacity: 0; transform: translateY(3px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .stock-table tr:hover td {
        background: #fbfcfe;
      }

      .stock-table tr.row-low td {
        background: #fffaf3;
      }

      .stock-table tr.row-empty td {
        background: #fff7f7;
      }

      .stock-table tr.row-checked td {
        background: #f1f8f4;
      }

      .stock-table tr.row-checked:hover td {
        background: #e8f5ee;
      }

      .stock-table .center {
        text-align: center;
      }

      .stock-table .right {
        text-align: right;
      }

      .product-cell {
        display: grid;
        gap: 2px;
      }

      .product-cell strong {
        color: var(--stock-text);
        font-size: 13px;
        font-weight: 800;
      }

      .verified-badge {
        width: max-content;
        margin-top: 2px;
        padding: 2px 6px;
        border: 1px solid #cce8d5;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: var(--stock-green);
        background: #f1f8f4;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: .02em;
        text-transform: uppercase;
      }

      .verified-badge svg {
        width: 11px;
        height: 11px;
      }

      .product-cell span {
        color: var(--stock-muted);
        font-size: 12px;
        font-weight: 650;
      }

      .meta-line {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        color: var(--stock-muted);
      }

      .meta-line i {
        width: 4px;
        height: 4px;
        border-radius: 999px;
        background: #cbd5e1;
      }

      .meta-line em {
        padding: 2px 6px;
        border: 1px solid var(--stock-border-soft);
        border-radius: 7px;
        color: var(--stock-text);
        background: #f8fafd;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 11px;
        font-style: normal;
        font-weight: 700;
      }

      .loja-badge {
        padding: 4px 8px;
        border: 1px solid #d2e3fc;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        color: var(--stock-blue);
        background: #f8fbff;
        font-size: 11px;
        font-weight: 750;
      }

      .thumb-cell {
        width: 38px;
        height: 38px;
        margin: 0 auto;
        padding: 0;
        border: 1px solid var(--stock-border-soft);
        border-radius: 11px;
        display: grid;
        place-items: center;
        overflow: hidden;
        color: var(--stock-soft);
        background: #f8fafd;
        font-family: inherit;
      }

      button.thumb-cell {
        cursor: pointer;
      }

      .thumb-cell.has-image:hover {
        border-color: #d2e3fc;
        box-shadow: 0 0 0 3px rgba(26, 115, 232, .10);
      }

      .thumb-cell.empty {
        cursor: default;
      }

      .thumb-cell img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .thumb-cell span {
        display: grid;
        place-items: center;
        color: var(--stock-soft);
      }

      .image-picker {
        margin-bottom: 14px;
        padding: 10px;
        border: 1px solid var(--stock-border);
        border-radius: 14px;
        display: grid;
        grid-template-columns: 118px 1fr;
        gap: 12px;
        background: #fff;
      }

      .image-picker-preview {
        width: 118px;
        height: 92px;
        padding: 0;
        border: 1px dashed #cbd5e1;
        border-radius: 12px;
        display: grid;
        place-items: center;
        overflow: hidden;
        background: #f8fafd;
        font-family: inherit;
      }

      .image-picker-preview.clickable {
        cursor: pointer;
      }

      .image-picker-preview.clickable:hover {
        border-color: #d2e3fc;
        box-shadow: 0 0 0 3px rgba(26, 115, 232, .10);
      }

      .image-picker-preview:disabled {
        cursor: default;
      }

      .image-picker-preview img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .image-placeholder {
        display: grid;
        gap: 5px;
        place-items: center;
        color: var(--stock-soft);
        font-size: 11px;
        font-weight: 700;
      }

      .image-picker-info {
        min-width: 0;
        display: grid;
        align-content: center;
        gap: 6px;
      }

      .image-picker-info > span {
        color: var(--stock-text);
        font-size: 12px;
        font-weight: 800;
      }

      .image-picker-info > small {
        color: var(--stock-muted);
        font-size: 11px;
        line-height: 1.35;
      }

      .image-picker-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }

      .upload-btn {
        height: 34px;
        padding: 0 12px;
        border: 1px solid var(--stock-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        color: var(--stock-muted);
        background: #fff;
        font-size: 12px;
        font-weight: 750;
        cursor: pointer;
      }

      .upload-btn:hover {
        color: var(--stock-blue);
        border-color: #d2e3fc;
        background: #f8fbff;
      }

      .upload-btn input {
        display: none;
      }

      .image-viewer-overlay {
        position: fixed;
        inset: 0;
        z-index: 10020;
        padding: 22px;
        display: grid;
        place-items: center;
        background: rgba(32, 33, 36, .72);
      }

      .image-viewer-shell {
        width: min(980px, 96vw);
        max-height: 94vh;
        border: 1px solid rgba(255, 255, 255, .16);
        border-radius: 18px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: #fff;
      }

      .image-viewer-head {
        padding: 13px 14px;
        border-bottom: 1px solid var(--stock-border-soft);
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        background: #fff;
      }

      .image-viewer-head h2 {
        margin: 0;
        color: var(--stock-text);
        font-size: 18px;
        font-weight: 760;
        letter-spacing: -.025em;
      }

      .image-viewer-head p {
        margin: 3px 0 0;
        color: var(--stock-muted);
        font-size: 12px;
      }

      .image-viewer-body {
        min-height: 260px;
        max-height: calc(94vh - 78px);
        padding: 14px;
        display: grid;
        place-items: center;
        overflow: auto;
        background:
          linear-gradient(45deg, #f8fafd 25%, transparent 25%),
          linear-gradient(-45deg, #f8fafd 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, #f8fafd 75%),
          linear-gradient(-45deg, transparent 75%, #f8fafd 75%);
        background-color: #fff;
        background-size: 20px 20px;
        background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
      }

      .image-viewer-body img {
        max-width: 100%;
        max-height: calc(94vh - 110px);
        border-radius: 14px;
        object-fit: contain;
        display: block;
        background: #fff;
      }

      .qty-control {
        width: fit-content;
        margin: 0 auto;
        padding: 3px;
        border: 1px solid var(--stock-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: #fff;
      }

      .qty-control button {
        width: 25px;
        height: 25px;
        border: 1px solid var(--stock-border);
        border-radius: 999px;
        display: grid;
        place-items: center;
        color: var(--stock-muted);
        background: #fff;
        font-size: 13px;
        font-weight: 800;
        cursor: pointer;
      }

      .qty-control button:disabled {
        opacity: .4;
        cursor: not-allowed;
      }

      .qty-control strong {
        min-width: 26px;
        color: var(--stock-text);
        font-size: 13px;
        text-align: center;
      }

      .qty-print {
        display: none;
      }

      .price-cell {
        color: var(--stock-text);
        font-size: 12px;
        font-weight: 700;
      }

      .actions-cell {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: 5px;
      }

      .actions-cell button {
        width: 30px;
        height: 30px;
        border: 1px solid transparent;
        border-radius: 999px;
        display: grid;
        place-items: center;
        color: var(--stock-muted);
        background: transparent;
        cursor: pointer;
      }

      .actions-cell button:hover {
        color: var(--stock-blue);
        border-color: #d2e3fc;
        background: #f8fbff;
      }

      .actions-cell button.danger:hover {
        color: var(--stock-red);
        border-color: #fecaca;
        background: #fef2f2;
      }

      .actions-cell button.check-action {
        color: var(--stock-soft);
      }

      .actions-cell button.check-action.checked {
        color: #fff;
        border-color: var(--stock-green);
        background: var(--stock-green);
      }

      .actions-cell button.check-action.checked:hover {
        color: #fff;
        border-color: #137333;
        background: #137333;
      }

      .empty-table {
        height: 180px !important;
        color: var(--stock-muted) !important;
        text-align: center;
      }

      .stock-loading {
        min-height: 280px;
        padding: 24px;
        display: grid;
        place-items: center;
        align-content: center;
        gap: 9px;
        color: var(--stock-muted);
        background: #fff;
        font-size: 13px;
        text-align: center;
      }

      .thin-loader {
        width: 190px;
        height: 3px;
        border-radius: 999px;
        overflow: hidden;
        background: #e8eaed;
        position: relative;
      }

      .thin-loader::after {
        content: "";
        position: absolute;
        inset: 0;
        width: 40%;
        border-radius: inherit;
        background: var(--stock-blue);
        animation: loader-move 1.1s linear infinite;
      }

      @keyframes loader-move {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(260%); }
      }

      .modal-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        padding: 20px;
        display: grid;
        place-items: center;
        background: rgba(32, 33, 36, .60);
      }

      .modal-shell {
        max-height: 92vh;
        border: 1px solid var(--stock-border);
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: #fff;
      }

      .modal-head {
        padding: 14px;
        border-bottom: 1px solid var(--stock-border-soft);
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
      }

      .modal-head h2 {
        margin: 0;
        color: var(--stock-text);
        font-size: 18px;
        font-weight: 750;
        letter-spacing: -.02em;
      }

      .modal-head p {
        margin: 3px 0 0;
        color: var(--stock-muted);
        font-size: 12px;
      }

      .modal-body {
        padding: 14px;
        overflow: auto;
      }

      .modal-foot {
        padding: 12px 14px;
        border-top: 1px solid var(--stock-border-soft);
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
      }

      .soft-note {
        margin-bottom: 12px;
        padding: 10px;
        border: 1px solid #d2e3fc;
        border-radius: 11px;
        display: grid;
        gap: 3px;
        color: var(--stock-blue);
        background: #f8fbff;
        font-size: 12px;
        line-height: 1.4;
      }

      .soft-note strong {
        color: var(--stock-blue);
      }

      .soft-note small {
        color: var(--stock-muted);
      }

      .form-field {
        margin-bottom: 10px;
        display: grid;
        gap: 5px;
      }

      .form-field span {
        color: var(--stock-muted);
        font-size: 11px;
        font-weight: 750;
        letter-spacing: .02em;
        text-transform: uppercase;
      }

      .edit-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0 10px;
      }

      .print-header {
        display: none;
      }

      @media (max-width: 1100px) {
        .filters-line {
          grid-template-columns: 1fr 1fr;
        }

        .kpi-strip {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      @media (max-width: 680px) {
        .stock-top {
          align-items: stretch;
          flex-direction: column;
          padding: 10px;
        }

        .stock-top-left {
          flex-direction: column;
          gap: 8px;
        }

        .stock-main {
          padding: 0 10px 10px;
        }

        .top-actions {
          justify-content: flex-start;
        }

        .filters-line {
          grid-template-columns: 1fr;
        }

        .kpi-strip {
          grid-template-columns: 1fr;
        }

        .kpi-cell {
          border-right: 0;
          border-bottom: 1px solid var(--stock-border-soft);
        }

        .kpi-cell:last-child {
          border-bottom: 0;
        }

        .edit-grid {
          grid-template-columns: 1fr;
        }

        .image-picker {
          grid-template-columns: 1fr;
        }

        .image-picker-preview {
          width: 100%;
          height: 160px;
        }
      }

      @media print {
        @page {
          margin: 1cm;
          size: landscape;
        }

        body {
          background: #fff !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .no-print,
        .actions-cell,
        .actions-cell *,
        .qty-control {
          display: none !important;
        }

        .stock-page {
          background: #fff !important;
        }

        .stock-main {
          padding: 0 !important;
        }

        .print-header {
          margin-bottom: 16px;
          padding-bottom: 10px;
          border-bottom: 2px solid #000;
          display: flex !important;
          align-items: flex-end;
          justify-content: space-between;
          gap: 20px;
        }

        .print-header h1 {
          margin: 0;
          font-size: 22px;
        }

        .print-header p {
          margin: 3px 0;
          font-size: 12px;
        }

        .table-panel,
        .table-scroll {
          border: 0 !important;
          overflow: visible !important;
          max-height: none !important;
        }

        .stock-table {
          min-width: 0 !important;
          width: 100% !important;
          font-size: 10pt !important;
        }

        .stock-table th,
        .stock-table td {
          border: 1px solid #ddd !important;
          color: #000 !important;
          background: #fff !important;
          padding: 6px !important;
          height: auto !important;
        }

        .stock-table th {
          background: #f1f3f4 !important;
        }

        .qty-print {
          display: inline !important;
          font-weight: 700;
        }

        .loja-badge {
          border: 0 !important;
          background: transparent !important;
          color: #000 !important;
          padding: 0 !important;
        }
      }
    `}</style>
  );
}
